import { useAgGridRowListener } from "$root/third-party-integrations/ag-grid/ag-grid";
import type { SortCriterionDTO } from "@eu.mdotm.api/smart-workspaces";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { maybeCall, type SetterOrUpdaterParam } from "@mdotm/shadcn/legacy/utils";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import type { IRowNode } from "ag-grid-community";
import type { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect } from "react";
import { chartTypeSortingMode } from "../chart-type";
import { selectChart, selectCharts, selectSelection, useWorkspaceStore } from "../state";

export function workspaceHandlers() {
	function useSelectionChart() {
		const { dispatch, slices } = useWorkspaceStore();
		const selection = useAtomValue(slices.selection$);
		const setSelection = useCallback(
			(selection: string[]) => {
				void dispatch({ type: "setSelection", selection });
			},
			[dispatch],
		);

		return { selection, setSelection };
	}

	function useSelectionGrid<T extends { uuid: string }>(gridEl: AgGridReact<T> | null) {
		const { dispatch, readState, slices } = useWorkspaceStore();

		const selection = useAtomValue(slices.selection$);
		const pagination = useAtomValue(slices.pagination$);
		const refs = useUnsafeUpdatedRef({ gridEl, dispatch, readState });

		/** The current selection, for the updater form — a dispatch has no `(prev) => next` overload. */
		const applySelection = useCallback(
			(valueOrUpdater: SetterOrUpdaterParam<string[]>) => {
				const next = maybeCall(valueOrUpdater, selectSelection(refs.current.readState()));
				void refs.current.dispatch({ type: "setSelection", selection: next });
			},
			[refs],
		);

		const setSelection = useCallback(
			(valueOrUpdater: SetterOrUpdaterParam<string[]>, opt?: { skipGriSelectionMatch: boolean }) => {
				const newSelection = maybeCall(valueOrUpdater, selectSelection(refs.current.readState()));
				const nodesToSelect: IRowNode<T>[] = [];
				refs.current.gridEl?.api.forEachNode((node) => {
					if (node.data?.uuid && newSelection.includes(node.data.uuid)) {
						nodesToSelect.push(node);
					}
				});
				refs.current.gridEl?.api.deselectAll();
				refs.current.gridEl?.api.setNodesSelected({ nodes: nodesToSelect, newValue: true });
				applySelection(
					opt?.skipGriSelectionMatch ? newSelection : nodesToSelect.flatMap((x) => (x.data?.uuid ? [x.data.uuid] : [])),
				);
			},
			[refs, applySelection],
		);
		const view = useAtomValue(slices.view$);
		// biome-ignore lint/correctness/useExhaustiveDependencies: <sync only on view changed || pagination>
		useEffect(() => {
			const api = gridEl?.api;
			if (api) {
				const selectedRows: IRowNode<T>[] = [];
				api.forEachNode((node) => {
					if (node.data?.uuid && selection.includes(node.data.uuid)) {
						selectedRows.push(node);
					}
				});

				api.deselectAll();
				api.setNodesSelected({
					nodes: selectedRows,
					newValue: true,
				});
			}
		}, [gridEl, view, pagination.page]);

		useAgGridRowListener(gridEl, "selectionChanged", ({ api, source }) => {
			// ignore programmatic selection (the rowDataUpdated restore below, the view/pagination
			// re-sync effect, and setSelection) — only user-driven changes should write the selection
			if (source === "api") {
				return;
			}
			const currentGridSelection = api.getSelectedRows().map((row) => row.uuid);
			const rows = refs.current.gridEl?.props.rowData?.map((row) => row.uuid) ?? [];

			applySelection((latestSelection) => {
				const selectionNotInPage = latestSelection.filter((uuid) => !rows.includes(uuid));
				return [...currentGridSelection, ...selectionNotInPage];
			});
		});

		useAgGridRowListener(gridEl, "rowDataUpdated", ({ api }) => {
			const selectedRows: IRowNode<T>[] = [];
			api.forEachNode((node) => {
				if (node.data?.uuid && selection.includes(node.data.uuid)) {
					selectedRows.push(node);
				}
			});
			api.deselectAll();
			api.setNodesSelected({
				nodes: selectedRows,
				newValue: true,
			});
		});

		return { selection, setSelection };
	}

	function useSortChart() {
		const { dispatch, readState, slices } = useWorkspaceStore();
		const sort = useAtomValue(slices.sort$);
		const refs = useUnsafeUpdatedRef({ dispatch, readState });
		const setSort = useCallback(
			(chartIndex: number, sort: SortCriterionDTO) => {
				const chart = selectChart(refs.current.readState(), chartIndex);
				if (!chart) {
					throw new Error("chart not found");
				}

				void refs.current.dispatch({
					type: "editChart",
					index: chartIndex,
					concepts: [],
					chart: {
						preferences: chart.preferences
							? {
									...chart.preferences,
									sort: sort.direction,
								}
							: {
									hide: true,
									sort: sort.direction,
								},
					},
				});
				void refs.current.dispatch({ type: "setSort", sort });
			},
			[refs],
		);

		const sortingCleanUp = useCallback(
			(parameterizedConceptUuid?: string) => {
				const charts = selectCharts(refs.current.readState());
				for (let index = 0; index < charts.length; index++) {
					const chart = charts[index];
					// Only a chart that *drives* the workspace sort has a direction to keep consistent with it. The
					// stacked column persists its direction in the same preference, but its sort is a frontend view
					// over its own rows (see `chartTypeSortingMode`), so a workspace sort that moved elsewhere — or
					// was cleared — must not clear that one too.
					if (chartTypeSortingMode(chart.chartType) !== "WORKSPACE") {
						continue;
					}
					if (!chart.preferences?.sort) {
						continue;
					}

					if (!parameterizedConceptUuid || !chart.series.includes(parameterizedConceptUuid)) {
						void refs.current.dispatch({
							type: "editChart",
							index,
							concepts: [],
							chart: {
								preferences: chart.preferences
									? {
											...chart.preferences,
											sort: null,
										}
									: {
											hide: true,
											sort: null,
										},
							},
						});
					}
				}
			},
			[refs],
		);

		// biome-ignore lint/correctness/useExhaustiveDependencies: <sync sort>
		useEffect(() => {
			sortingCleanUp(sort?.parameterizedConceptUuid);
		}, [sort?.parameterizedConceptUuid]);

		return { sort, setSort };
	}

	function useSortGrid<T extends { uuid: string }>(gridEl: AgGridReact<T> | null) {
		const { dispatch, slices } = useWorkspaceStore();
		const sort = useAtomValue(slices.sort$);
		const view = useAtomValue(slices.view$);
		const setSort = useCallback(
			(sort: SortCriterionDTO | null) => {
				void dispatch({ type: "setSort", sort });
			},
			[dispatch],
		);

		// biome-ignore lint/correctness/useExhaustiveDependencies: <sync on view or sort change>
		useEffect(() => {
			const api = gridEl?.api;
			if (!api) {
				return;
			}
			// The sort slice is the single source of truth; mirror it onto the grid as the one clearing
			// writer (defaultState clears every other column, so only a single sort arrow can ever show).
			if (sort?.parameterizedConceptUuid && sort.direction) {
				api.applyColumnState({
					state: [{ colId: sort.parameterizedConceptUuid, sort: sort.direction === "ASC" ? "asc" : "desc" }],
					defaultState: { sort: null },
				});
			} else {
				api.applyColumnState({ defaultState: { sort: null } });
			}
		}, [gridEl, view, sort]);

		useAgGridRowListener(gridEl, "sortChanged", (params) => {
			// ignore programmatic sorts (e.g. our own applyColumnState) to avoid the
			// applyColumnState -> sortChanged -> setSort feedback loop
			if (params.source === "api") {
				return;
			}
			const sortedColumn = params.columns?.find((x) => x.getSort() != null);
			const newOrderBy = sortedColumn
				? ({
						direction: sortedColumn.getSort() === "asc" ? "ASC" : "DESC",
						parameterizedConceptUuid: sortedColumn.getColId(),
					} satisfies SortCriterionDTO)
				: null;
			// One dispatch, where this used to be an atom write plus a hand-rolled `updateUpstreamWorkspace`
			// and a hand-rolled `trackWorkspaceContentChange("setSort")`, both with a comment apologising for
			// bypassing the observer. The middleware does both now.
			//
			// The only sortable columns of this grid are the concept columns, whose `colId` *is* a pool uuid:
			// the ACTION column declares `sortable: false` and ag-grid's selection column defaults to it. So a
			// header sort cannot name a concept the pool lacks, which is what `setSort` now throws on.
			setSort(newOrderBy);
		});

		return { sort, setSort };
	}

	return { useSelectionChart, useSelectionGrid, useSortGrid, useSortChart };
}
